import React from 'react';
import { Container, Typography, Box, Divider } from '@mui/material';
import { useLanguage } from '../contexts/LanguageContext';
import SEOHead from './SEOHead';
import { useSiteContent, tr } from '../hooks/useSiteContent';
import { LEGAL_DEFAULTS } from '../content/legal';
import type { LegalKey } from '../content/legal';

interface LegalPageProps {
  contentKey: LegalKey;
  seoTitleKey: string;
  canonicalPath: string;
}

const LegalPage: React.FC<LegalPageProps> = ({ contentKey, seoTitleKey, canonicalPath }) => {
  const { currentLanguage, t } = useLanguage();
  const content = useSiteContent(contentKey, LEGAL_DEFAULTS[contentKey]);
  const isAr = currentLanguage === 'ar';
  const sections = content.sections[currentLanguage] ?? content.sections.de ?? [];

  return (
    <>
      <SEOHead
        title={t(seoTitleKey)}
        description={t(seoTitleKey.replace(/_title$/, '_description'))}
        canonicalPath={canonicalPath}
        noindex
      />
      <Container maxWidth="md" sx={{ py: 10, dir: isAr ? 'rtl' : 'ltr' }}>
        <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 3, display: 'block', mb: 2 }}>
          {tr(content.overline, currentLanguage)}
        </Typography>
        <Typography variant="h2" component="h1" sx={{ mb: 6 }}>
          {tr(content.title, currentLanguage)}
        </Typography>

        {sections.map((section, idx) => {
          const blocks = section.text.split(/\n\s*\n/).filter((b) => b.trim() !== '');
          return (
            <Box key={idx} sx={{ mb: 5 }}>
              <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 700, color: '#1a2640' }}>
                {section.heading}
              </Typography>
              {blocks.map((block, bi) => {
                const lines = block.split('\n');
                return lines.length > 1 ? (
                  <Box key={bi} sx={{ pl: blocks.length > 1 ? 2 : 0, mb: 1.5 }}>
                    {lines.map((line, li) => (
                      <Typography key={li} variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 0.5 }}>
                        {line}
                      </Typography>
                    ))}
                  </Box>
                ) : (
                  <Typography key={bi} variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 1.5 }}>
                    {block}
                  </Typography>
                );
              })}
              {idx < sections.length - 1 && <Divider sx={{ mt: 4 }} />}
            </Box>
          );
        })}
      </Container>
    </>
  );
};

export default LegalPage;
